/**
 * ChannelTile — Marketplace channel tile
 *
 * Shows upsell mode for unconnected channels (ghost sparkline + CTA).
 * Shows live data mode for connected channels (today's orders/revenue + real sparkline).
 */

import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { CARD } from "@/config/design-tokens";
import { PulseSparkline } from "./PulseSparkline";
import type { ChannelSummary } from "@/features/channels/api/marketplaceTypes";
import type { SparklinePoint } from "@/hooks/usePulseSparkline";
import { CHANNEL_BRAND_ICON, type ChannelPlatform } from "@/features/channels/components/ChannelBrandIcons";

type Platform = ChannelPlatform;

const PLATFORMS: Record<
  Platform,
  { label: string; color: string; headline: string; subtitle: string }
> = {
  shopee: {
    label: "Shopee",
    color: "#EE4D2D",
    headline: "จัดการออเดอร์ Shopee",
    subtitle: "ซิงค์สินค้าและจัดส่งจากที่เดียว",
  },
  lazada: {
    label: "Lazada",
    color: "#171EB5",
    headline: "จัดการออเดอร์ Lazada",
    subtitle: "ซิงค์สินค้าและจัดส่งจากที่เดียว",
  },
  tiktok: {
    label: "TikTok Shop",
    color: "#000000",
    headline: "จัดการออเดอร์ TikTok",
    subtitle: "ซิงค์สินค้าและจัดส่งจากที่เดียว",
  },
};

const ICONS = CHANNEL_BRAND_ICON;

interface ChannelTileProps {
  platform: Platform;
  isConnected?: boolean;
  summary?: ChannelSummary;
}

export function ChannelTile({ platform, isConnected, summary }: ChannelTileProps) {
  const { label, color, headline, subtitle } = PLATFORMS[platform];
  const Icon = ICONS[platform];

  const sparklineData: SparklinePoint[] =
    isConnected && summary?.sparkline_7d
      ? summary.sparkline_7d.map((value) => ({ date: "", value }))
      : [];

  // Period totals from API (always show period data, not just today)
  const periodOrders = summary?.week_orders ?? summary?.sparkline_7d?.reduce((a, b) => a + b, 0) ?? 0;
  const periodRevenue = summary?.week_revenue ?? 0;

  const linkTo = isConnected ? `/channels/${platform}` : "/apps";

  return (
    <Link
      to={linkTo}
      className={cn(
        CARD, "shadow-none",
        "relative min-w-0 p-4",
        "transition-all duration-200",
        "hover:scale-[1.02] hover:shadow-apple-sm active:scale-[0.98]"
      )}
      aria-label={
        isConnected
          ? `${label} — ${summary?.today_orders ?? 0} orders today`
          : `${label} — ${headline} — Connect`
      }
    >
      <div className="relative z-10 flex flex-col min-h-[160px]">
        <div className="flex items-center gap-2">
          <Icon className="w-5 h-5 flex-shrink-0" color={color} />
          <p className="text-[12px] font-semibold tracking-wide text-foreground">
            {label}
          </p>
        </div>

        {isConnected && summary ? (
          <>
            <p className="text-[22px] font-semibold text-foreground mt-2">
              {periodOrders.toLocaleString()}
              <span className="text-[12px] font-normal text-muted-foreground ml-1">
                orders
              </span>
            </p>
            <p className="text-[13px] text-muted-foreground">
              {`฿${periodRevenue.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`}
            </p>
          </>
        ) : (
          <>
            <p className="text-[13px] font-medium text-foreground leading-snug mt-2">
              {headline}
            </p>
            <p className="text-[11px] text-muted-foreground mt-0.5">
              {subtitle}
            </p>
          </>
        )}

        <div className="mt-auto">
          <span
            className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-[11px] font-medium text-white"
            style={{ backgroundColor: color }}
          >
            {isConnected ? "ดูรายละเอียด" : "เชื่อมต่อ"}
            <ArrowRight className="w-3 h-3" />
          </span>
        </div>
      </div>
      <div className="absolute bottom-0 left-0 right-0 h-[55%]">
        <PulseSparkline
          data={sparklineData}
          isZero={!isConnected}
          metric={platform}
        />
      </div>
    </Link>
  );
}
